/**
 * Subscription API client
 */
import apiClient from './client';

export interface SubscriptionStatus {
  status: string;
  subscription_type: string | null;
  billing_period: string | null;
  subscription_tier: string;
  access_expires_at: string | null;
  days_remaining: number | null;
  is_grace_period: boolean;
  grace_period_ends_at: string | null;
  grace_days_remaining: number | null;
  last_payment_date: string | null;
  payment_failed_at: string | null;
}

export interface SubscriptionCreateRequest {
  tier_code: string;
  is_recurring: boolean;
}

export interface SubscriptionResponse {
  success: boolean;
  message: string;
  subscription_status: SubscriptionStatus;
  payment_url?: string;
}

// ============================================================================
// Public Plans Types
// ============================================================================

export interface TierLimits {
  clipboard_slots: number;
  conversations_limit: number;
  ai_requests_limit: number;
  file_upload_limit: number;
  storage_limit_mb: number;
}

export interface PublicTier {
  tier_code: string;
  display_name: string;
  description: string | null;
  tier_type: 'individual' | 'organization';
  price_monthly: number | null;
  price_yearly: number | null;
  trial_days: number;
  limits: TierLimits;
  features: Record<string, boolean>;
  max_subusers: number | null;
  chargekeep_recurring_form_url: string | null;
  chargekeep_yearly_form_url: string | null;
  chargekeep_onetime_form_url: string | null;
}

export interface PublicPlansResponse {
  individual_plans: PublicTier[];
  organization_plans: PublicTier[];
}

export interface CurrentSubscriptionTier {
  tier_code: string;
  display_name: string;
  tier_type: string;
  limits: TierLimits;
  features: Record<string, boolean>;
}

export interface OrganizationInfo {
  id: string;
  name: string;
  slug: string;
  subscription_tier: string;
  subscription_status: string;
  is_owner: boolean;
  user_role: string;
}

export interface CurrentSubscriptionResponse {
  status: string;
  subscription_type: string | null;
  billing_period: string | null;
  access_expires_at: string | null;
  days_remaining: number | null;
  is_grace_period: boolean;
  grace_period_ends_at: string | null;
  grace_days_remaining: number | null;
  last_payment_date: string | null;
  current_tier: CurrentSubscriptionTier;
  organization: OrganizationInfo | null;
}

/**
 * Get current subscription status
 */
export const getSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
  const response = await apiClient.get<SubscriptionStatus>('/api/subscriptions/status');
  return response.data;
};

/**
 * Create a new subscription (recurring or one-time)
 */
export const createSubscription = async (
  tierCode: string,
  isRecurring: boolean = true
): Promise<SubscriptionResponse> => {
  const response = await apiClient.post<SubscriptionResponse>('/api/subscriptions/create', {
    tier_code: tierCode,
    is_recurring: isRecurring,
  });
  return response.data;
};

/**
 * Cancel current subscription
 */
export const cancelSubscription = async (): Promise<SubscriptionResponse> => {
  const response = await apiClient.delete<SubscriptionResponse>('/api/subscriptions/cancel');
  return response.data;
};

/**
 * Sync customer profile to ChargeKeep
 */
export const syncCustomerProfile = async (): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post('/api/subscriptions/sync-customer');
  return response.data;
};

/**
 * Get all public subscription plans (no auth required)
 */
export const getPublicPlans = async (): Promise<PublicPlansResponse> => {
  const response = await apiClient.get<PublicPlansResponse>('/api/subscriptions/plans');
  return response.data;
};

/**
 * Get current user's subscription with full tier details
 */
export const getCurrentSubscription = async (): Promise<CurrentSubscriptionResponse> => {
  const response = await apiClient.get<CurrentSubscriptionResponse>('/api/subscriptions/current');
  return response.data;
};
